import React, { useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@growthaxl/web-foundation';
import { MessageInput } from '../MessageInput';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { useFileUpload } from '../../hooks/useFileUpload';
import { useEsaAttachmentStore } from '../../store/esaAttachmentStore';
import { FileAttachmentManager } from './FileAttachmentManager';
import { AttachmentChipList } from './AttachmentChipList';
import { DropZone } from '../DropZone';
import type { ConverseAttachment } from '../../constants/fileUploadConstants';

export interface ESAComposerProps {
  draft: string;
  onDraftChange: (draft: string) => void;
  /** When isAttachEnabled, called with (message, attachments[]). */
  onSend: (message: string, attachments?: ConverseAttachment[]) => void | Promise<void>;
  isAttachEnabled: boolean;
  isStreaming: boolean;
  /** When true, the entire composer (textarea + send + mic) is non-interactive. */
  disabled?: boolean;
  className?: string;
}

/** Shared style for the attach/mic action buttons — 30×30, rounded-[7px]. */
const ACTION_BTN_BASE = 'inline-flex shrink-0 items-center justify-center w-[30px] h-[30px] rounded-[7px] cursor-pointer transition-colors';

interface MicButtonProps {
  disabled: boolean;
  isRecording: boolean;
  isSupported: boolean;
  onClick: () => void;
}

function MicButton({ disabled, isRecording, isSupported, onClick }: MicButtonProps): React.ReactElement | null {
  const { t } = useTranslation();

  if (!isSupported) return null;

  const label = isRecording
    ? t('esa.stopRecording', 'Stop recording')
    : t('esa.startRecording', 'Start voice input');

  return (
    <button
      type="button"
      disabled={disabled && !isRecording}
      aria-label={label}
      aria-pressed={isRecording}
      onClick={onClick}
      className={[
        ACTION_BTN_BASE,
        isRecording
          ? 'bg-error text-white animate-pulse'
          : 'bg-transparent text-text-secondary hover:bg-surface-hover',
        (disabled && !isRecording) ? 'opacity-50 cursor-not-allowed' : '',
      ].join(' ')}
    >
      <Icon name="microphone" className="!size-[18px] shrink-0" />
    </button>
  );
}

export function ESAComposer({
  draft,
  onDraftChange,
  onSend,
  isAttachEnabled,
  isStreaming,
  disabled = false,
  className,
}: ESAComposerProps): React.ReactElement {
  const { handleFilesSelected, handleRemove, handleRetry } = useFileUpload();
  const attachments = useEsaAttachmentStore(s => s.attachments);
  const hasInFlightUploads = useEsaAttachmentStore(s => s.hasInFlightUploads);
  const getReadyAttachments = useEsaAttachmentStore(s => s.getReadyAttachments);
  const clearAttachments = useEsaAttachmentStore(s => s.clearAttachments);

  const isSendBlocked = disabled || isStreaming || (isAttachEnabled && hasInFlightUploads());

  // Tracks the draft value at the moment recording started so interim results
  // can be appended without clobbering text the user typed beforehand.
  const draftBaseRef = useRef<string>('');

  const handleTranscript = useCallback(
    (text: string, isFinal: boolean): void => {
      if (isFinal) {
        const committed = `${draftBaseRef.current}${text} `;
        draftBaseRef.current = committed;
        onDraftChange(committed);
      } else {
        onDraftChange(`${draftBaseRef.current}${text}`);
      }
    },
    [onDraftChange]
  );

  const { isSupported, isRecording, start, stop } = useSpeechRecognition({
    onTranscript: handleTranscript,
  });

  const handleMicClick = useCallback((): void => {
    if (isRecording) {
      stop();
    } else {
      draftBaseRef.current = draft.endsWith(' ') || draft === '' ? draft : `${draft} `;
      start();
    }
  }, [isRecording, draft, start, stop]);

  const handleSend = useCallback(
    (message: string): void => {
      if (isSendBlocked) return;
      const readyAtts = isAttachEnabled ? getReadyAttachments() : undefined;
      void onSend(message, readyAtts && readyAtts.length > 0 ? readyAtts : undefined);
      if (isAttachEnabled) clearAttachments();
    },
    [isSendBlocked, isAttachEnabled, onSend, getReadyAttachments, clearAttachments]
  );

  const handleDraftChange = useCallback(
    (nextDraft: string): void => {
      onDraftChange(nextDraft);
      draftBaseRef.current = nextDraft;
    },
    [onDraftChange]
  );

  const composerContent = (
    <MessageInput
      value={draft}
      onChange={handleDraftChange}
      onSend={handleSend}
      disabled={isSendBlocked}
      className={className}
      topSlot={
        isAttachEnabled && attachments.length > 0 ? (
          <AttachmentChipList
            attachments={attachments}
            onRemove={handleRemove}
            onRetry={handleRetry}
          />
        ) : undefined
      }
      actionsSlot={
        <>
          {isAttachEnabled && (
            <FileAttachmentManager onFilesSelected={handleFilesSelected} />
          )}
          <MicButton
            disabled={isSendBlocked}
            isRecording={isRecording}
            isSupported={isSupported}
            onClick={handleMicClick}
          />
        </>
      }
    />
  );

  if (isAttachEnabled) {
    return (
      <DropZone
        enabled={isAttachEnabled}
        onFilesDropped={files => void handleFilesSelected(files)}
      >
        {composerContent}
      </DropZone>
    );
  }

  return composerContent;
}
